/**
 * GhostLinks — thin SVG curves connecting same-module pairs across the two
 * panes of a SplitDiffBoard.
 *
 * Modules in each pane are tagged with `data-module-id`. For every id that
 * appears in both panes AND whose ChangeSet status is `modified` or
 * `renamed`, we draw a cubic-bezier curve from the right edge of the left
 * widget to the left edge of the right widget. The overlay is positioned
 * absolutely inside the containing element so it can span the gap between
 * panes without blocking pointer events.
 */
import { useEffect, useState, type RefObject } from "react";
import { useDiffLookup } from "./useDiffLookup.js";
import { stableId } from "@archlang/engine";

export function GhostLinks({ containerRef }: { containerRef: RefObject<HTMLElement | null> }) {
  const lookup = useDiffLookup();
  const [paths, setPaths] = useState<string[]>([]);

  useEffect(() => {
    if (!lookup || !containerRef.current) {
      setPaths([]);
      return;
    }
    const root = containerRef.current;
    const ds: string[] = [];
    const nodes = root.querySelectorAll<HTMLElement>("[data-module-id]");
    const byId = new Map<string, HTMLElement[]>();
    nodes.forEach((n) => {
      // Skip the gravity-well context layer's copies (the out-of-focus parent
      // surround behind each focal). They share module ids with the focal
      // widgets, so counting them would draw ghost curves across the dimmed
      // surround / between the two panes' hole frames.
      if (n.closest("[data-board-context]")) return;
      const id = n.dataset.moduleId;
      if (!id) return;
      const arr = byId.get(id) ?? [];
      arr.push(n);
      byId.set(id, arr);
    });
    const rootRect = root.getBoundingClientRect();
    for (const [id, els] of byId) {
      if (els.length !== 2) continue;
      const status = lookup.entityStatus({ kind: "module", id: stableId(id) });
      if (status !== "modified" && status !== "renamed") continue;
      const ra = els[0].getBoundingClientRect();
      const rb = els[1].getBoundingClientRect();
      const x1 = ra.right - rootRect.left;
      const y1 = ra.top + ra.height / 2 - rootRect.top;
      const x2 = rb.left - rootRect.left;
      const y2 = rb.top + rb.height / 2 - rootRect.top;
      const cx = (x1 + x2) / 2;
      ds.push(`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`);
    }
    setPaths(ds);
  }, [lookup, containerRef]);

  return (
    <svg
      className="diff-ghost-links"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        width: "100%",
        height: "100%",
      }}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} stroke="#f59e0b" strokeWidth={1} fill="none" opacity={0.4} />
      ))}
    </svg>
  );
}
